import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createInitialFormData, useEmployeeFormHandlers } from './CreateEmployeeForm';
import { useUserRoles, useCreateEmployee } from '../../hooks';
import { EmployeeCreateResponse } from '../../api/employees';
import { CreateEmployeeFormContent } from './CreateEmployeeFormContent';
import { SidebarContent } from '@/components/layout/sidepanel/SidebarFooter';
import { useAuth } from '@/lib';
import { hasFeature } from '@/domain/authentication';
import type { LinkedUser } from '../../types';

interface CreateContactProps {
    companyId: string;
    onSuccess: () => void;
    // Preselected user account, set when the form is opened from a user profile
    linkedUser?: LinkedUser;
}

export function CreateContact({ companyId, onSuccess, linkedUser }: CreateContactProps) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const {
        formData, setFormData,
        errors, setErrors,
        handleChange, handleToggleMainContact, handleSyncEmails,
        handleSelectUser, handleClearUser,
        isFormValid,
    } = useEmployeeFormHandlers(undefined, undefined, linkedUser);

    const canLinkUser = !!user && hasFeature(user, 'superuser.forceLinkUser');
    const { data: roles, isLoading: rolesLoading } = useUserRoles(companyId);
    const createEmployee = useCreateEmployee(companyId);

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            if (!isFormValid) return;

            setErrors({});

            // A linked user account replaces the invite email
            const userAccount = formData.linkedUser
                ? { userId: formData.linkedUser.userId }
                : { userEmail: formData.userEmail };

            try {
                const response = await createEmployee.mutateAsync({
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phoneNumber: formData.phoneNumber || undefined,
                    email: formData.email,
                    roleId: formData.roleId,
                    isMainContact: formData.isMainContact,
                    userType: formData.userType,
                    ...userAccount,
                });

                if (response === EmployeeCreateResponse.OK) {
                    setFormData(createInitialFormData(undefined, undefined, linkedUser));
                    onSuccess?.();
                } else if (response === EmployeeCreateResponse.EMAIL_EXISTS) {
                    setErrors((prev) => ({ ...prev, general: t('employees.createForm.emailExists') }));
                } else {
                    setErrors((prev) => ({ ...prev, general: t('employees.createForm.unknownError') }));
                }
            } catch {
                setErrors((prev) => ({ ...prev, general: t('employees.createForm.unknownError') }));
            }
        },
        [isFormValid, formData, createEmployee, onSuccess, linkedUser, t]
    );

    return (<SidebarContent>
        <form onSubmit={handleSubmit} className="space-y-6">
            <CreateEmployeeFormContent
                formData={formData}
                errors={errors}
                roles={roles}
                rolesLoading={rolesLoading}
                handleChange={handleChange}
                handleToggleMainContact={handleToggleMainContact}
                handleSyncEmails={handleSyncEmails}
                isFormValid={isFormValid}
                isSubmitting={createEmployee.isPending}
                canLinkUser={canLinkUser}
                handleSelectUser={handleSelectUser}
                handleClearUser={handleClearUser}
                isLinkedUserLocked={!!linkedUser}
            />
        </form>
    </SidebarContent>
    );
}
